import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';

import { Combobox } from '@headlessui/react';
import { Dialog } from '@reach/dialog';

import assetsDataFull from '@/data/supported-assets-data.json';
import { CoinData } from '@/store/home/types';

interface AssetSearchWidgetProps {
  assets: { ticker: string; name: string; price: string }[];
  onSearch?: () => void;
  isInWidget?: boolean;
  onClose?: () => void;
}

const assetsData = assetsDataFull.map((asset) => ({
  ticker: asset.ticker,
  name: asset.name,
  price: '',
}));

// Custom debounce hook
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

function AssetSearchWidget({ assets, onSearch, isInWidget, onClose }: AssetSearchWidgetProps) {
  const [inputValue, setInputValue] = useState('');
  const searchQuery = useDebounce(inputValue, 300);

  // Only filter when in widget mode
  const displayedAssets = useMemo(() => {
    if (!isInWidget || !searchQuery) return assets;

    const query = searchQuery.toLowerCase().trim();
    return assets.filter(
      (asset) =>
        asset.name.toLowerCase().includes(query) || asset.ticker.toLowerCase().includes(query),
    );
  }, [assets, searchQuery, isInWidget]);

  return (
    <div
      className={`relative w-full rounded-[20px] border-2 border-[#6066A3]/50 bg-gradient-to-br from-[#1e233f]/15 to-[#162056] p-2 shadow-[0px_1px_68.5999984741211px_0px_rgba(219,233,249,0.20)] sm:p-4 md:w-[472px] ${isInWidget ? 'h-[600px]' : ''}`}
    >
      <div
        className='absolute left-1/2 top-1/2 z-[-10] -translate-x-1/2 -translate-y-1/2 rounded-[369.869px] bg-gradient-to-r from-[#493DB5] to-[#6DABD4] opacity-80 mix-blend-plus-lighter blur-[162.35000610351562px]'
        style={{ filter: 'blur(162.35000610351562px)', width: '353.218px', height: '369.869px' }}
      ></div>
      <div className='flex items-center justify-between pb-3 sm:pb-6'>
        <div className='font-sans text-base font-bold leading-[1.25em] text-white -sm:pl-2 -sm:pt-2'>
          Search for supported assets
        </div>
        {isInWidget && (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            className='h-4 w-4 cursor-pointer'
            onClick={onClose}
          >
            <path
              d='M4.26671 12.6667L3.33337 11.7334L7.06671 8.00004L3.33337 4.26671L4.26671 3.33337L8.00004 7.06671L11.7334 3.33337L12.6667 4.26671L8.93337 8.00004L12.6667 11.7334L11.7334 12.6667L8.00004 8.93337L4.26671 12.6667Z'
              fill='white'
            />
          </svg>
        )}
      </div>
      <div
        className={`flex flex-col gap-2 rounded-[20px] bg-[#0e1024] p-2 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] sm:gap-4 sm:p-4 ${isInWidget ? 'h-[535px] sm:h-[520px]' : ''}`}
      >
        {!isInWidget ? (
          <div
            className='flex h-8 w-full cursor-pointer items-center justify-center rounded-[15px] bg-[#212336]'
            onClick={onSearch}
          >
            <div className='flex w-full items-center gap-2 rounded-[15px] px-4 py-2'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                className='h-4 w-4'
              >
                <path
                  d='M10.6294 10.3053L13.3354 12.9997M12.0018 6.99967C12.0018 4.42235 9.91246 2.33301 7.33512 2.33301C4.7578 2.33301 2.66846 4.42235 2.66846 6.99967C2.66846 9.57701 4.7578 11.6663 7.33512 11.6663C9.91246 11.6663 12.0018 9.57701 12.0018 6.99967Z'
                  stroke='#797B89'
                  strokeWidth='0.833333'
                  strokeLinecap='round'
                />
              </svg>
              <div className='font-sans text-xs font-medium leading-[1.333em] text-[#797b89]'>
                Search
              </div>
            </div>
          </div>
        ) : (
          <div className='relative w-full'>
            <input
              type='text'
              className='w-full rounded-[15px] border-none bg-[#212336] py-2 pl-10 pr-4 text-sm text-white placeholder-[#797b89] focus:outline-none focus:ring-1 focus:ring-[#6066A3]'
              placeholder='Search assets...'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <div className='absolute inset-y-0 left-0 flex items-center pl-3'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                className='h-4 w-4'
              >
                <path
                  d='M10.6294 10.3053L13.3354 12.9997M12.0018 6.99967C12.0018 4.42235 9.91246 2.33301 7.33512 2.33301C4.7578 2.33301 2.66846 4.42235 2.66846 6.99967C2.66846 9.57701 4.7578 11.6663 7.33512 11.6663C9.91246 11.6663 12.0018 9.57701 12.0018 6.99967Z'
                  stroke='#797B89'
                  strokeWidth='0.833333'
                  strokeLinecap='round'
                />
              </svg>
            </div>
          </div>
        )}

        <div className='font-heading text-[13.35px] font-medium leading-[1.429em] text-[#F3FAFF]'>
          {isInWidget ? 'Results' : 'Popular'}
        </div>
        <div className={`${isInWidget ? 'flex-1 overflow-y-auto pr-4' : ''}`}>
          <div className='flex flex-col gap-[9.73px]'>
            {displayedAssets.map((asset, index) => (
              <div key={`${asset.ticker}-${asset.name}-${index}`}>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-[12.97px]'>
                    <div className='relative h-[38.91px] w-[38.91px] overflow-hidden rounded-[8.92px] border border-[#ffffff]/30'>
                      <ExportedImage
                        placeholder='empty'
                        src={`/images/lp/coins/icons/${asset.ticker.toLowerCase()}.png`}
                        alt={asset.ticker}
                        className='absolute inset-0 m-auto h-[24.32px] w-[24.32px] rounded-[25.34px] object-contain'
                        width={40}
                        height={40}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/images/lp/supported-assets/misc/dummy.png';
                        }}
                      />
                    </div>
                    <div className='flex flex-col gap-[3.24px]'>
                      <div className='font-sans text-[14.78px] font-bold leading-[1.176em] text-white'>
                        {asset.ticker.toUpperCase()}
                      </div>
                      <div className='font-sans text-[12.35px] font-medium leading-[1.429em] text-[#bdbdbd]'>
                        {asset.name}
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col items-end gap-[3.24px]'>
                    <div className='text-right font-sans text-[14.59px] font-bold leading-[1.111em] text-[#e6bc41]'>
                      ${asset.price}
                    </div>
                  </div>
                </div>
                <div className='mt-2 h-0 w-full outline outline-[0.81px] outline-offset-[-0.41px] outline-[#355983]/30'></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const formatPrice = (price: string | undefined): string => {
  if (!price) return '';
  const fixedPrice = parseFloat(price).toFixed(2);
  return fixedPrice === '0.00' ? parseFloat(price).toExponential(2) : fixedPrice;
};

export default function HeroWidget({ data }: { data?: CoinData }) {
  const popularTickers = useMemo(() => ['btc', 'eth', 'bnb', 'kmd'], []);
  const [allAssets, setAllAssets] = useState(assetsData);
  const [popularAssets, setPopularAssets] = useState<
    { ticker: string; name: string; price: string }[]
  >([
    { ticker: 'btc', name: 'Bitcoin', price: '' },
    { ticker: 'eth', name: 'Ethereum', price: '' },
    { ticker: 'bnb', name: 'Binance Coin', price: '' },
    { ticker: 'kmd', name: 'Komodo', price: '' },
  ]);

  useEffect(() => {
    if (data) {
      // Update popular assets
      setPopularAssets(
        popularTickers.map((ticker) => ({
          ticker: ticker,
          name: popularAssets.filter(
            (asset) => asset.ticker.toUpperCase() == ticker.toUpperCase(),
          )[0].name,
          price: formatPrice(data[ticker.toUpperCase()]?.last_price),
        })),
      );

      // Update all assets
      setAllAssets(
        assetsData
          .filter((asset) => Object.keys(data).includes(asset.ticker.toUpperCase()))
          .map((asset) => ({
            ...asset,
            price: formatPrice(data[asset.ticker.toUpperCase()]?.last_price),
          })),
      );
    }
  }, [data, popularTickers]);

  const [isSupportedAssetsModalOpen, setIsSupportedAssetsModalOpen] = useState(false);

  const closeSupportedAssetsModal = () => {
    setIsSupportedAssetsModalOpen(false);
  };

  const openSupportedAssetsModal = () => {
    setIsSupportedAssetsModalOpen(true);
  };

  const supportedAssetsModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeModalIfClickOutside = (event: MouseEvent) => {
      if (
        supportedAssetsModalRef.current &&
        !supportedAssetsModalRef.current?.contains(event.target as Node)
      ) {
        closeSupportedAssetsModal();
      }
    };
    document.addEventListener('mousedown', closeModalIfClickOutside);

    return () => {
      document.removeEventListener('mousedown', closeModalIfClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSupportedAssetsModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSupportedAssetsModalOpen]);

  return (
    <div className='relative flex w-full items-center justify-center lg:justify-end'>
      <AssetSearchWidget assets={popularAssets} onSearch={openSupportedAssetsModal} />
      <Dialog
        isOpen={isSupportedAssetsModalOpen}
        onDismiss={closeSupportedAssetsModal}
        className='fixed inset-0 z-[100] overflow-hidden'
        aria-label='Supported Assets'
      >
        <div className='fixed inset-0 bg-black bg-opacity-90' />
        <div className='fixed inset-0 flex items-center justify-center p-2 sm:p-4'>
          <div
            className='relative flex items-center justify-center lg:justify-end'
            ref={supportedAssetsModalRef}
          >
            <div
              className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[369.869px] bg-gradient-to-r from-[#493DB5] to-[#6DABD4] opacity-80 mix-blend-plus-lighter blur-[162.35000610351562px]'
              style={{
                filter: 'blur(162.35000610351562px)',
                width: '353.218px',
                height: '369.869px',
              }}
            ></div>
            <AssetSearchWidget
              assets={allAssets}
              isInWidget={true}
              onClose={closeSupportedAssetsModal}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
